import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/Layout/AdminLayout';
import { toast } from 'react-toastify';
import { 
    fetchOrderById, 
    fetchAllCategories, 
    fetchProductsByCategory, 
    updateSaleOrder, 
    SalesPageUpdateRequest, 
    Category, 
    Product 
} from '../api/api'; 
import { FaTrashAlt, FaCalculator, FaSave, FaArrowLeft } from 'react-icons/fa';

interface SelectedProduct extends Product {
  uniqueId: number;
  quantity: number;
  itemDiscountMode: 'PERCENTAGE' | 'AMOUNT';
  itemDiscountValue: number;
  finalItemPrice: number;
}

const UpdateOrderPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>(); 
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);

  // --- Customer & Address State ---
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState(''); 
  const [email, setEmail] = useState('');
  const [alternateMobile, setAlternateMobile] = useState('');
  
  const [houseNumber, setHouseNumber] = useState('');
  const [streetArea, setStreetArea] = useState('');
  const [landmark, setLandmark] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [country, setCountry] = useState('');
  
  const [remark, setRemark] = useState('');

  // --- Product Logic State ---
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [rootCategories, setRootCategories] = useState<Category[]>([]);
  
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<number | null>(null);
  
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);

  // --- Global Discount State ---
  const [globalDiscountMode, setGlobalDiscountMode] = useState<'PERCENTAGE' | 'AMOUNT'>('PERCENTAGE');
  const [globalDiscountInput, setGlobalDiscountInput] = useState<number>(0);

  // 1. INITIAL DATA LOAD
  useEffect(() => {
    const init = async () => {
        if (!orderId) return;
        setLoading(true);
        try {
            // A. Categories Load
            const catRes = await fetchAllCategories();
            setAllCategories(catRes.data);
            setRootCategories(catRes.data.filter((c: Category) => !c.parentId));

            // B. Existing Order Load
            const orderRes = await fetchOrderById(orderId);
            const data = orderRes.data; 

            // C. Pre-fill Address
            const u = data.mainUser;
            setName(u ? `${u.firstName} ${u.lastName || ''}` : (data.customerName || ''));
            setMobile(u?.mobile || data.mobile || '');
            setEmail(u?.email || data.email || '');
            
            setAlternateMobile(data.alternateMobile || '');
            setHouseNumber(data.houseNumber || '');
            setStreetArea(data.streetArea || '');
            setLandmark(data.landmark || '');
            setCity(data.city || '');
            setState(data.state || '');
            setPincode(data.pincode || '');
            setCountry(data.country || '');
            setRemark(data.remark || '');

            // D. Pre-fill Products
            if (data.medicine) {
                const existingItem: SelectedProduct = {
                    id: 0, 
                    productName: data.medicine, 
                    measurementUnit: data.medicineUnit || '',
                    typeQuantity: '', 
                    price: data.medicinePrice || 0,
                    
                    uniqueId: Date.now(),
                    quantity: data.medicineQuantity || 1,
                    itemDiscountMode: 'PERCENTAGE',
                    itemDiscountValue: 0, 
                    finalItemPrice: data.payableAmount || 0
                };
                
                if (data.discountPercentage && data.discountPercentage > 0) {
                   setGlobalDiscountMode('PERCENTAGE');
                   setGlobalDiscountInput(data.discountPercentage);
                }
                
                setSelectedProducts([existingItem]);
            }

        } catch (e) {
            console.error(e);
            toast.error("Failed to load order data");
            navigate('/admin/verified-orders');
        } finally {
            setLoading(false);
        }
    };
    init();
  }, [orderId, navigate]);

  // 2. HELPER: SubCategories Filtering
  const subCategories = useMemo(() => {
      if (!selectedCategoryId) return [];
      return allCategories.filter(c => c.parentId === selectedCategoryId);
  }, [selectedCategoryId, allCategories]);

  // 3. HELPER: Load Products on SubCategory Select
  const loadSubCatProducts = async (subId: number) => {
      setSelectedSubCategoryId(subId);
      try {
          const res = await fetchProductsByCategory(subId);
          setAvailableProducts(res.data);
      } catch (err) {
          toast.error("Failed to fetch products");
      }
  };

  // 4. ACTION: Add Product to Table
  const handleAddProduct = (p: Product) => {
      if (selectedProducts.length > 0) {
          if (!window.confirm("Replacing existing product. Continue?")) return;
          setSelectedProducts([]);
      }

      const newItem: SelectedProduct = {
          ...p,
          uniqueId: Date.now(),
          quantity: 1,
          itemDiscountMode: 'PERCENTAGE',
          itemDiscountValue: 0,
          finalItemPrice: p.price
      };
      setSelectedProducts([newItem]); 
  };

  // 5. CALCULATION LOGIC
  const updateRow = (uid: number, field: string, val: any) => {
      setSelectedProducts(prev => prev.map(item => {
          if (item.uniqueId !== uid) return item;
          const updated = { ...item, [field]: val };
          
          const base = updated.price * updated.quantity;
          let disc = updated.itemDiscountMode === 'PERCENTAGE' 
             ? (base * updated.itemDiscountValue) / 100 
             : updated.itemDiscountValue;
          
          if (disc > base) disc = base;
          
          updated.finalItemPrice = base - disc;
          return updated;
      }));
  };

  const totals = useMemo(() => {
      const subTotal = selectedProducts.reduce((sum, p) => sum + p.finalItemPrice, 0);
      const originalTotal = selectedProducts.reduce((sum, p) => sum + (p.price * p.quantity), 0);
      
      let globalDisc = 0;
      if (globalDiscountMode === 'PERCENTAGE') {
          globalDisc = (subTotal * globalDiscountInput) / 100;
      } else {
          globalDisc = globalDiscountInput;
      }

      if (globalDisc > subTotal) globalDisc = subTotal;
      
      const payable = subTotal - globalDisc;
      return { 
          originalTotal, 
          payable: Math.round(payable), 
          discountAmount: originalTotal - payable,
          discountPercentage: originalTotal > 0 ? ((originalTotal - payable)/originalTotal * 100) : 0
      };
  }, [selectedProducts, globalDiscountMode, globalDiscountInput]);

  // 6. SUBMIT
  const handleUpdate = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!orderId) return;
      setLoading(true);

      const payload: SalesPageUpdateRequest = {
          name, email, mobile, alternateMobile,
          houseNumber, streetArea, landmark, city, state, pincode, country,
          remark,
          categoryId: selectedCategoryId, 
          subCategoryId: selectedSubCategoryId,
          
          medicine: selectedProducts.map(p => `${p.productName} (Qty: ${p.quantity})`).join(', '),
          medicineUnit: selectedProducts.map(p => `${p.typeQuantity}${p.measurementUnit}`).join(', '),
          medicinePrice: Math.round(totals.originalTotal),
          medicineQuantity: selectedProducts.reduce((sum, p) => sum + p.quantity, 0),
          
          discountPercentage: parseFloat(totals.discountPercentage.toFixed(2)),
          discountAmount: parseFloat(totals.discountAmount.toFixed(2)),
          paidAmount: totals.payable,
          payableAmount: totals.payable,
          
          orderItems: [] 
      };

      try {
          await updateSaleOrder(orderId, payload);
          toast.success("Order Updated Successfully");
          navigate('/admin/verified-orders'); 
      } catch (err) {
          toast.error("Update Failed");
      } finally {
          setLoading(false);
      }
  };

  if (loading) return <AdminLayout><div className="p-8 text-gray-900 dark:text-white bg-gray-50 dark:bg-gray-900 min-h-screen">Loading Order...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen text-gray-900 dark:text-white transition-colors duration-200">
        <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-yellow-600 dark:text-yellow-500">Edit Order</h2>
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                <FaArrowLeft /> Back
            </button>
        </div>
        
        <form onSubmit={handleUpdate} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* LEFT: Customer Info */}
            <div className="lg:col-span-1 space-y-6">
                 <div className="bg-white dark:bg-gray-800 p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg">
                    <h3 className="font-bold text-lg text-green-600 dark:text-green-400 mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">Customer Info</h3>
                    <div className="space-y-3">
                        <div>
                            <label className="text-xs text-gray-500 dark:text-gray-400">Name</label>
                            <input value={name} readOnly className="input-field opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-700" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 dark:text-gray-400">Mobile</label>
                            <input value={mobile} readOnly className="input-field opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-700" />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 dark:text-gray-400">Alt Mobile</label>
                            <input value={alternateMobile} onChange={e=>setAlternateMobile(e.target.value)} className="input-field" />
                        </div>
                    </div>
                 </div>

                 <div className="bg-white dark:bg-gray-800 p-5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg">
                    <h3 className="font-bold text-lg text-green-600 dark:text-green-400 mb-4 border-b border-gray-200 dark:border-gray-600 pb-2">Address</h3>
                    <div className="space-y-3">
                        <input value={houseNumber} onChange={e=>setHouseNumber(e.target.value)} className="input-field" placeholder="House No" />
                        <input value={streetArea} onChange={e=>setStreetArea(e.target.value)} className="input-field" placeholder="Street / Area" />
                        <div className="grid grid-cols-2 gap-2">
                            <input value={city} onChange={e=>setCity(e.target.value)} className="input-field" placeholder="City" />
                            <input value={state} onChange={e=>setState(e.target.value)} className="input-field" placeholder="State" />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <input value={pincode} onChange={e=>setPincode(e.target.value)} className="input-field" placeholder="Pincode" />
                            <input value={country} onChange={e=>setCountry(e.target.value)} className="input-field" placeholder="Country" />
                        </div>
                    </div>
                 </div>
            </div>

            {/* RIGHT: Product & Calculation */}
            <div className="lg:col-span-2 bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 h-fit shadow-lg">
                <h3 className="font-bold text-lg text-green-600 dark:text-green-400 mb-4 flex items-center gap-2">
                    <FaCalculator/> Update Products
                </h3>
                
                {/* 1. Selection Area */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <select onChange={e => setSelectedCategoryId(Number(e.target.value))} className="input-field">
                        <option value="">Select Category</option>
                        {rootCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <select onChange={e => loadSubCatProducts(Number(e.target.value))} disabled={!selectedCategoryId} className="input-field">
                        <option value="">Select Sub Category</option>
                        {subCategories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>

                {/* 2. Available List */}
                {availableProducts.length > 0 && (
                    <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded mb-6 max-h-40 overflow-y-auto custom-scrollbar p-2">
                        {availableProducts.map(p => (
                            <div key={p.id} className="flex justify-between items-center p-2 hover:bg-gray-200 dark:hover:bg-gray-800 border-b border-gray-200 dark:border-gray-800 last:border-0">
                                <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">{p.productName}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{p.typeQuantity}{p.measurementUnit} | ₹{p.price}</p>
                                </div>
                                <button type="button" onClick={() => handleAddProduct(p)} className="text-xs bg-blue-600 px-3 py-1 rounded hover:bg-blue-500 text-white font-bold">
                                    Add +
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* 3. Selected Table */}
                <div className="overflow-x-auto custom-scrollbar bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700 mb-6">
                    <table className="w-full text-sm text-gray-700 dark:text-gray-300">
                        <thead className="bg-gray-100 dark:bg-gray-800 text-xs uppercase text-gray-500 dark:text-gray-400">
                            <tr>
                                <th className="p-3 text-left">Product</th>
                                <th className="p-3 text-center">Unit</th>
                                <th className="p-3 text-center">Price</th>
                                <th className="p-3 text-center w-20">Qty</th>
                                <th className="p-3 text-center w-24">Disc</th>
                                <th className="p-3 text-right">Total</th>
                                <th className="p-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedProducts.map(item => (
                                <tr key={item.uniqueId} className="border-b border-gray-200 dark:border-gray-800 hover:bg-gray-200 dark:hover:bg-gray-800/50 transition">
                                    <td className="p-3 font-medium text-gray-900 dark:text-white">{item.productName}</td>
                                    <td className="p-3 text-center text-xs text-gray-500">{item.typeQuantity}{item.measurementUnit}</td>
                                    <td className="p-3 text-center">₹{item.price}</td>
                                    <td className="p-3 text-center">
                                        <input type="number" min="1" value={item.quantity} 
                                            onChange={e => updateRow(item.uniqueId, 'quantity', Number(e.target.value))} 
                                            className="w-16 bg-white dark:bg-gray-700 text-center rounded border border-gray-300 dark:border-gray-600 p-1 text-gray-900 dark:text-white"
                                        />
                                    </td>
                                    <td className="p-3 text-center">
                                         <div className="flex gap-1 justify-center">
                                            <select 
                                                value={item.itemDiscountMode}
                                                onChange={(e) => updateRow(item.uniqueId, 'itemDiscountMode', e.target.value)}
                                                className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-[10px] rounded px-1 text-gray-900 dark:text-white"
                                            >
                                                <option value="PERCENTAGE">%</option>
                                                <option value="AMOUNT">₹</option>
                                            </select>
                                            <input type="number" min="0" value={item.itemDiscountValue} 
                                                onChange={e => updateRow(item.uniqueId, 'itemDiscountValue', Number(e.target.value))} 
                                                className="w-12 bg-white dark:bg-gray-700 text-center rounded border border-gray-300 dark:border-gray-600 p-1 text-xs text-gray-900 dark:text-white"
                                            />
                                         </div>
                                    </td>
                                    <td className="p-3 text-right font-bold text-green-600 dark:text-green-400">₹{item.finalItemPrice.toFixed(2)}</td>
                                    <td className="p-3 text-center">
                                        <button type="button" onClick={() => setSelectedProducts(prev => prev.filter(x => x.uniqueId !== item.uniqueId))} className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300">
                                            <FaTrashAlt/>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {selectedProducts.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="p-4 text-center text-gray-500 italic">No products added yet.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* 4. Totals */}
                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded border border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-500 dark:text-gray-400 text-sm">Global Discount:</span>
                        <div className="flex items-center gap-2">
                             <div className="bg-white dark:bg-gray-700 rounded p-1 flex text-xs border border-gray-200 dark:border-gray-600">
                                <button type="button" onClick={()=>setGlobalDiscountMode('PERCENTAGE')} className={`px-2 py-0.5 rounded ${globalDiscountMode==='PERCENTAGE'?'bg-blue-600 text-white':''}`}>%</button>
                                <button type="button" onClick={()=>setGlobalDiscountMode('AMOUNT')} className={`px-2 py-0.5 rounded ${globalDiscountMode==='AMOUNT'?'bg-blue-600 text-white':''}`}>₹</button>
                             </div>
                             <input type="number" value={globalDiscountInput} onChange={e => setGlobalDiscountInput(Number(e.target.value))} className="w-20 bg-white dark:bg-gray-700 text-center rounded border border-gray-300 dark:border-gray-600 text-sm p-1 text-gray-900 dark:text-white"/>
                        </div>
                    </div>
                    
                    <div className="flex justify-between text-gray-500 dark:text-gray-400 text-sm mb-1">
                        <span>Original Total:</span>
                        <span>₹{totals.originalTotal}</span>
                    </div>
                    
                    <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700 mt-2">
                        <span className="text-xl font-bold text-gray-900 dark:text-white">PAYABLE AMOUNT</span>
                        <span className="text-2xl font-bold text-green-600 dark:text-green-400">₹{totals.payable}</span>
                    </div>
                </div>

                <div className="mt-6">
                    <button type="submit" disabled={loading} className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 rounded-lg shadow-lg flex justify-center items-center gap-2">
                        <FaSave /> {loading ? 'Saving...' : 'Update & Save Order'}
                    </button>
                </div>
            </div>
        </form>
      </div>
      <style>{`
        .input-field { width: 100%; padding: 10px; border-radius: 6px; background: #ffffff; color: #111827; border: 1px solid #d1d5db; outline: none; transition: all 0.2s; }
        .dark .input-field { background: #374151; color: white; border-color: #4b5563; }
        .input-field:focus { border-color: #60a5fa; box-shadow: 0 0 0 2px rgba(96, 165, 250, 0.2); }
        .input-field:disabled { opacity: 0.6; cursor: not-allowed; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f3f4f6; }
        .dark .custom-scrollbar::-webkit-scrollbar-track { background: #111827; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #374151; border-radius: 3px; }
      `}</style>
    </AdminLayout>
  );
};

export default UpdateOrderPage;